import React, { useEffect, useMemo, useState } from 'react'
import { DashboardModal } from '@uppy/react'
import { Uppy } from '@uppy/core'
import Transloadit from '@uppy/transloadit'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/image-editor/dist/style.css'
import { AuthModel, getAuth } from '../../auth'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { getUploadSign, recordFile, saveFile } from '../core/_requests'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { useAuth } from '../../auth'

export const FileUpload = ({
  fileSize,
  maxFileNumber,
  allowType,
  metaData,
  modalStatus,
  handleClose,
  handleSuccess,
  showCompatibilityMsg = true,
  resourceType,
  assemblyOption = 'default', // options: default, rk-doc-upload
  isZipFile = false,
  isNewVersionFile = false,
  recordId,
  isPublic = false,
  company,
  isSingleFile = false,
  enforceAspectRatio = '',
}: {
  fileSize: number
  maxFileNumber: number
  allowType: string[]
  metaData: {}
  modalStatus: boolean
  handleClose: () => void
  handleSuccess: (id: number, name: string) => void
  showCompatibilityMsg?: boolean
  resourceType: string
  assemblyOption?: string
  isZipFile?: boolean
  isNewVersionFile?: boolean
  recordId?: string
  isPublic?: boolean
  company?: number
  isSingleFile?: boolean
  enforceAspectRatio?: string
}) => {
  const { companyId } = useAuth()
  const { token } = (getAuth() as AuthModel) || {}
  const [isUnmount, setIsUnmount] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false) // State to track upload status

  if (!token) {
    getAuth()
  }

  const { formatMessage } = useIntl()

  const getAssemblyOptions = async (): Promise<any> => {
    try {
      const resp = await getUploadSign(
        resourceType,
        assemblyOption,
        companyId ? companyId : company
      )
      const transloaditObject = resp.data.data

      let params = {
        auth: {
          key: transloaditObject.transloaditKey,
          expires: transloaditObject.expires,
        },
        steps: {},
      }

      if (assemblyOption === 'default') {
        params.steps = {
          ':original': {
            robot: '/upload/handle',
          },
          image_optimized: {
            use: ':original',
            robot: '/image/optimize',
            priority: 'compression-ratio',
          },
          described: {
            use: ':original',
            robot: '/image/describe',
            provider: 'aws',
            explicit_descriptions: true,
            format: 'meta',
            granularity: 'list',
            result: true,
          },
          filtered: {
            result: true,
            use: 'described',
            robot: '/file/filter',
            // eslint-disable-next-line
            declines: [['${file.meta.descriptions}', 'includes', 'Nudity']],
            error_on_decline: true,
            error_msg: 'Explicit content is not allowed!',
          },
          exported: transloaditObject.s3PrivateBucketObj,
        }
      } else if (assemblyOption === 'rk-doc-upload') {
        params.steps = {
          ':original': {
            robot: '/upload/handle',
          },
          exported: transloaditObject.s3PrivateBucketObj,
        }
      }

      return {
        params: params,
        signature: transloaditObject.privateUploadSignature,
      }
    } catch (err: any) {
      console.log(err)

      const errorMsg = err.message.includes('Explicit content')
        ? 'Explicit content is not allowed!'
        : formatMessage({
          id: 'Server error while uploading. Please try again later.',
        })
      toast.error(errorMsg, {
        autoClose: false,
        closeButton: true, // Display close button (cross icon)
      })
      uppy.cancelAll()
      handleClose()
      throw err // Propagate the error
    }
  }

  const uppy = useMemo(() => {
    const uppyInstance = new Uppy({
      autoProceed: false,
      debug: false,
      restrictions: {
        maxFileSize: fileSize,
        maxNumberOfFiles: maxFileNumber,
        allowedFileTypes: allowType,
      },
      meta: metaData,
      // onBeforeFileAdded: (file: any, files: any) => {
      //   if (resourceType === 'user_profile' || resourceType === 'company_profile') {
      //     const image = new Image()
      //     image.src = URL.createObjectURL(file.data)
      //     image.onload = () => {
      //       if (image.width !== image.height) {
      //         uppyInstance.removeFile(file.id)
      //         uppyInstance.info(
      //           'Please upload a square image for better compatibility.',
      //           'error',
      //           3000
      //         )
      //       }
      //     }
      //     image.onerror = () => {
      //       uppyInstance.removeFile(file.id)
      //       uppyInstance.info('Invalid image file!', 'error', 3000)
      //     }
      //   }
      //   return true
      // },
      onBeforeFileAdded: (file: any, files: any) => {
        const shouldValidate =
          resourceType === 'user_profile' || resourceType === 'company_profile'

        if (shouldValidate && enforceAspectRatio) {
          const image = new Image()
          const objectUrl = URL.createObjectURL(file.data)
          image.src = objectUrl

          image.onload = () => {
            const { width, height } = image
            const aspectRatio = width / height
            let valid = true

            if (enforceAspectRatio === 'square') {
              valid = width === height
            } else if (enforceAspectRatio === '16:9') {
              const expectedRatio = 16 / 9
              const tolerance = 0.05 // ±5%
              valid = Math.abs(aspectRatio - expectedRatio) < tolerance
            }

            if (!valid) {
              uppyInstance.removeFile(file.id)
              uppyInstance.info(
                `Please upload an image with a ${enforceAspectRatio} aspect ratio.`,
                'error',
                3000
              )
            }

            URL.revokeObjectURL(objectUrl)
          }

          image.onerror = () => {
            uppyInstance.removeFile(file.id)
            uppyInstance.info('Invalid image file!', 'error', 3000)
            URL.revokeObjectURL(objectUrl)
          }
        }

        return true
      }
    })
      .use(Transloadit, {
        assemblyOptions: async () => {
          return await getAssemblyOptions()
        },
        waitForEncoding: true,
      })
      .on('upload', () => {
        setIsUploading(true) // Set uploading state to true when upload starts
        const uploadButton = document.querySelector('.uppy-StatusBar-actionBtn--upload')

        if (uploadButton) {
          uploadButton.setAttribute('disabled', 'true')
            ; (uploadButton as HTMLElement).style.backgroundColor = '#4776e69e' // Change button color to gray when disabled
          uploadButton.textContent = `Please wait...` // Change button text to "Uploading..."
        }
      })
      .on('transloadit:complete', async (assembly: any) => {
        try {
          setIsUploading(false) // Set uploading state to false when upload completes
          const uploadButton = document.querySelector('.uppy-Dashboard-uploadButton')
          if (uploadButton) {
            uploadButton.innerHTML = `Upload` // Reset button text
            uploadButton.removeAttribute('disabled') // Enable button
          }

          let files = []
          if (assemblyOption === 'default') {
            files = assembly.results[':original']
          } else if (assemblyOption === 'rk-doc-upload') {
            files = assembly.results[':original']
          }
          const filesData = files.map((file: any) => {
            const fileUrl = file.ssl_url
            const path = new URL(fileUrl).pathname
            const cleanPath = path.startsWith('/') ? path.slice(1) : path

            return {
              path: cleanPath,
              fileName: file.name,
              sizeInBytes: file.size,
              ...metaData,
            }
          })

          if (assemblyOption === 'default') {
            const {
              data: { success, errors, data },
            } = await saveFile({ files: filesData })
            if (success) {
              const fileIds = data.map((file: any) => file.fileId)
              fileIds.forEach((fileId: any, index: any) => {
                handleSuccess(fileId, filesData[index].path)
              })
              setIsUnmount(true)
              handleClose()
              TagManager.dataLayer({
                dataLayer: {
                  event: GTMEvent.FileUploadSuccessful,
                  action: 'normal',
                  metadata: {
                    fileName: fileIds,
                  },
                },
              })
            } else {
              TagManager.dataLayer({
                dataLayer: {
                  event: GTMEvent.FileUploadFailed,
                  action: 'normal',
                  metadata: {
                    error: errors[0],
                  },
                },
              })
            }
          } else if (assemblyOption === 'rk-doc-upload') {
            if (isZipFile) {
              handleSuccess(1, filesData[0].path)
              handleClose()
            } else if (isPublic) {
              filesData.map((file: any, index: any) => {
                return handleSuccess(index, file)
              })
              uppy.clear()
              handleClose()
            } else {
              const {
                data: { success, errors, data },
              } = await recordFile({ files: filesData }, companyId, isNewVersionFile, recordId)
              if (success) {
                if (isSingleFile) {
                  handleSuccess(data.recordId, filesData[0].path)
                } else {
                  const fileIds = data.map((file: any) => file.fileId)

                  fileIds.forEach((fileId: any, index: any) => {
                    handleSuccess(fileId, filesData[index].path)
                  })
                }
                uppy.clear()
                handleClose()
              } else {
                toast.error(errors[0], {
                  autoClose: false,
                  closeButton: true,
                })
                TagManager.dataLayer({
                  dataLayer: {
                    event: GTMEvent.FileUploadFailed,
                    action: 'normal',
                    metadata: {
                      error: errors[0],
                    },
                  },
                })
              }
            }
          }
        } catch (error) {
          setIsUploading(false) // Ensure uploading state is reset on error
          const uploadButton = document.querySelector('.uppy-Dashboard-uploadButton')
          if (uploadButton) {
            uploadButton.innerHTML = `Upload` // Reset button text
            uploadButton.removeAttribute('disabled') // Enable button
          }
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.FileUploadFailed,
              action: 'normal',
              metadata: {
                error: error,
              },
            },
          })
        }
      })
      .on('error', (err: any) => {
        setIsUploading(false) // Reset uploading state on error
        const uploadButton = document.querySelector('.uppy-Dashboard-uploadButton')
        if (uploadButton) {
          uploadButton.innerHTML = `Upload` // Reset button text
          uploadButton.removeAttribute('disabled') // Enable button
        }
        let errorMsg = err.message?.includes('Explicit content')
          ? 'Explicit content is not allowed!'
          : formatMessage({
            id: 'Server error while uploading. Please try again later.',
          })

        toast.error(errorMsg, {
          autoClose: false,
          closeButton: true, // Display close button (cross icon)
        })
        uppyInstance.cancelAll()
      })

    return uppyInstance
  }, [assemblyOption]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => uppy.clear()
  }, [uppy, isUnmount]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DashboardModal
        className='setting-img-upload-modal'
        uppy={uppy}
        proudlyDisplayPoweredByUppy={false}
        closeModalOnClickOutside={!isUploading}
        open={modalStatus}
        onRequestClose={handleClose}
        plugins={['ImageEditor']}
      />
      <style>
        {`
          .uppy-Dashboard-inner{
            padding: 8px;
            background: #ffffff;
          }
          .uppy-DashboardContent-bar{
            background: #FFFFFF;
          }
          .uppy-Dashboard-poweredByUppy {
            display: none;
          }
          .uppy-u-reset {
            margin-bottom: 8px;
          }
          .uppy-StatusBar.is-waiting .uppy-StatusBar-actions{
            margin-top: 14px;
            background-color: #ffffff;
          }
          .uppy-u-reset Span {
            font-size: 24px;
          }
          .uppy-StatusBar.is-waiting .uppy-StatusBar-actionBtn--upload{
            background-color: #4776e6
          }
          .uppy-StatusBar.is-waiting .uppy-StatusBar-actionBtn--upload:hover{
            background-color: #4776e6
          }
        `}
      </style>
    </>
  )
}
